import React, { useState } from 'react';
import SalvPage from '../common/SalvPage';
import { scenarioInternalApi } from '../apis';
import { useLocation, useNavigate, useParams } from 'react-router';
import {
  IconColor,
  MenuItem,
  SalvButton,
  SalvLabel,
  SalvMenuButton,
  SalvTooltipWrapper,
  Size,
} from '@salvapp/ui-components';
import './MonitoringScenario.scss';
import FieldEditor, { FieldView } from './FieldEditor';
import { showConfirmationModal } from '../confirmation-modal/confirmationModalReducer';
import { useDispatch } from 'react-redux';
import { monitoringScenarioTypes } from './MonitoringScenarios';
import {
  FindScenarioMetricsPeriodTypeEnum,
  InternalApiReviewRequestEntityType,
  InternalApiReviewStatus,
  InternalApiScenario,
  InternalApiScenarioStatus,
  InternalApiScenarioVersionStatus,
} from '../api-internal';
import { convertToDropdownOption } from '../common/helper';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';
import ScenarioHistoryModal from './ScenarioHistoryModal';
import ScenarioVersions from './versions/ScenarioVersions';
import { useMe } from '../organisation/useMe';
import {
  SCENARIO_QUERY_KEY,
  useQueryScenario,
  useQueryScenarioCategories,
  useQueryScenarioMetrics,
  useQueryScenarioTypologies,
  useUpdateScenario,
} from './scenarioQueryActions';
import { CAN_DELETE_SCENARIO, CAN_MODIFY_SCENARIO } from '../auth/permissions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { useResolveReviewRequest, useReviewRequest } from '../review-requests/reviewRequestActions';
import { useQueryClient } from '@tanstack/react-query';

export const statusSwitchers = (status: InternalApiScenarioStatus, isReview: boolean, isAuthor: boolean) => {
  if (isReview && status === InternalApiScenarioStatus.ACTIVE && isAuthor) {
    return {
      message: 'This scenario is staged for deactivation.',
      label: <SalvLabel variant='success'>Active</SalvLabel>,
      actions: [
        {
          action: 'Withdraw',
          confirmationQuestion: 'Are you sure you want to keep the scenario active?',
          confirmationDetails: 'This scenario will keep generating alerts.',
          confirmationText: 'Yes, withdraw',
          nextStatus: InternalApiReviewStatus.WITHDRAWN,
        },
      ],
    };
  }
  if (isReview && status === InternalApiScenarioStatus.ACTIVE && !isAuthor) {
    return {
      message: 'This scenario is staged for deactivation.',
      label: <SalvLabel variant='success'>Active</SalvLabel>,
      actions: [
        {
          action: 'Approve',
          confirmationQuestion: 'Are you sure you want to deactivate the scenario?',
          confirmationDetails:
            'This scenario will stop generating alerts, which might have serious impact on your compliance operations.',
          confirmationText: 'Yes, deactivate',
          nextStatus: InternalApiReviewStatus.APPROVED,
        },
        {
          action: 'Reject',
          confirmationQuestion: 'Are you sure you want to keep the scenario active?',
          confirmationDetails: 'This scenario will keep generating alerts.',
          confirmationText: 'Yes, reject deactivation',
          nextStatus: InternalApiReviewStatus.REJECTED,
        },
      ],
    };
  }
  if (isReview && status === InternalApiScenarioStatus.INACTIVE && isAuthor) {
    return {
      message: 'This scenario is staged for activation.',
      label: <SalvLabel variant='danger'>Inactive</SalvLabel>,
      actions: [
        {
          action: 'Withdraw',
          confirmationQuestion: 'Are you sure you want to keep the scenario inactive?',
          confirmationDetails: 'This scenario will not generate alerts.',
          confirmationText: 'Yes, withdraw',
          nextStatus: InternalApiReviewStatus.WITHDRAWN,
        },
      ],
    };
  }
  if (isReview && status === InternalApiScenarioStatus.INACTIVE && !isAuthor) {
    return {
      message: 'This scenario is staged for activation.',
      label: <SalvLabel variant='danger'>Inactive</SalvLabel>,
      actions: [
        {
          action: 'Approve',
          confirmationQuestion: 'Are you sure you want to activate the scenario?',
          confirmationDetails:
            'This scenario will start generating alerts, which might have serious impact on your compliance operations.',
          confirmationText: 'Yes, activate',
          nextStatus: InternalApiReviewStatus.APPROVED,
        },
        {
          action: 'Reject',
          confirmationQuestion: 'Are you sure you want to keep the scenario inactive?',
          confirmationDetails: 'This scenario will not generate alerts.',
          confirmationText: 'Yes, reject activation',
          nextStatus: InternalApiReviewStatus.REJECTED,
        },
      ],
    };
  }

  if (status === InternalApiScenarioStatus.ARCHIVED) {
    return {
      message: 'This scenario is archived and no longer in use.',
      label: <SalvLabel variant='secondary'>Archived</SalvLabel>,
      actions: [
        {
          action: 'Restore',
          confirmationQuestion: 'Are you sure you want to restore the scenario?',
          confirmationDetails: 'The scenario will be put into inactive status.',
          confirmationText: 'Yes, restore',
          nextStatus: InternalApiScenarioStatus.INACTIVE,
        },
      ],
    };
  } else if (status === InternalApiScenarioStatus.ACTIVE) {
    return {
      message: 'This scenario is live and generating alerts.',
      label: <SalvLabel variant='success'>Active</SalvLabel>,
      actions: [
        {
          action: 'Deactivate',
          confirmationQuestion: 'Are you sure you want to deactivate the scenario?',
          confirmationDetails:
            'This scenario will stop generating alerts, which might have serious impact on your compliance operations.',
          confirmationText: 'Yes, deactivate',
          nextStatus: InternalApiScenarioStatus.INACTIVE,
        },
      ],
    };
  } else if (status === InternalApiScenarioStatus.INACTIVE) {
    return {
      message: 'This scenario is not live and is not generating alerts.',
      label: <SalvLabel variant='danger'>Inactive</SalvLabel>,
      actions: [
        {
          action: 'Activate',
          confirmationQuestion: 'Are you sure you want to activate the scenario?',
          confirmationDetails:
            'This scenario will start generating alerts, which might have serious impact on your compliance operations.',
          confirmationText: 'Yes, activate',
          nextStatus: InternalApiScenarioStatus.ACTIVE,
        },
      ],
    };
  }

  throw new Error('Should not be reached');
};

const MonitoringScenario = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { handle } = useParams() as { handle: string };
  const { features, permissions, id: userId, currentOrganisation } = useMe();
  const { data: reviewRequest } = useReviewRequest(InternalApiReviewRequestEntityType.SCENARIO, handle);
  const inReview = !!reviewRequest;
  const isAuthor = reviewRequest?.createdBy.id === userId;

  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const { data: scenario } = useQueryScenario(handle);
  const queryClient = useQueryClient();
  const { mutate: resolveReviewRequest } = useResolveReviewRequest(undefined, () => {
    queryClient.invalidateQueries({ queryKey: [SCENARIO_QUERY_KEY, scenario?.id, currentOrganisation?.id] });
  });
  const { data: scenarioMetrics30 } = useQueryScenarioMetrics(
    handle,
    FindScenarioMetricsPeriodTypeEnum.ONE_MONTH_BEFORE
  );
  const { data: scenarioMetrics90 } = useQueryScenarioMetrics(
    handle,
    FindScenarioMetricsPeriodTypeEnum.TWO_TO_FOUR_MONTHS_BEFORE
  );

  const { data: categories } = useQueryScenarioCategories();
  const { data: typologies } = useQueryScenarioTypologies(scenario?.category?.id);

  const { mutate: updateScenario } = useUpdateScenario();

  const isCounterpartyEnabled = isFeatureEnabled(ORGANISATION_FEATURE.COUNTERPARTY, features);

  const canModifyScenario = permissions.includes(CAN_MODIFY_SCENARIO);
  const canDeleteScenario = permissions.includes(CAN_DELETE_SCENARIO);

  const archivable = (scenario: InternalApiScenario) =>
    scenario.deletable && scenario.status !== InternalApiScenarioStatus.ARCHIVED;

  const truePositiveChange =
    (scenarioMetrics30 &&
      scenarioMetrics90 &&
      scenarioMetrics30.truePositivePercentage - scenarioMetrics90.truePositivePercentage) ??
    0;

  const meaningfulAlertChange =
    (scenarioMetrics30 &&
      scenarioMetrics90 &&
      scenarioMetrics30.meaningfulAlertPercentage - scenarioMetrics90.meaningfulAlertPercentage) ??
    0;

  const renderPercentChange = (percentChange: number) => {
    return percentChange < 0 ? `- ${Math.abs(percentChange).toFixed(2)}%` : `+ ${percentChange.toFixed(2)}%`;
  };

  const getChangeClass = (change: number) => {
    switch (true) {
      case change > 0:
        return 'positive-change change text-xs';
      case change < 0:
        return 'negative-change change text-xs';
      default:
        return 'no-change change text-xs';
    }
  };

  return (
    scenario && (
      <SalvPage
        title={`Scenario: ${scenario?.name}`}
        breadcrumb={{
          title: 'Scenario list',
          to: { pathname: '/scenarios', search: location.state?.search },
        }}
        actions={
          <SalvMenuButton
            ariaLabel={'More options'}
            size={Size.SMALL}
            onAction={async (value) => {
              if (value[0] === 'archive') {
                dispatch(
                  showConfirmationModal({
                    title: 'Are you sure you want to archive this scenario?',
                    description: 'You can always restore it later.',
                    onConfirm: async () => {
                      updateScenario({
                        id: scenario.id!,
                        internalApiUpdateScenario: {
                          status: InternalApiScenarioStatus.ARCHIVED,
                        },
                      });
                    },
                    confirmButtonVariant: 'danger',
                    iconColor: IconColor.DANGER,
                    confirmationText: 'Yes, archive',
                    cancelText: 'No',
                  })
                );
              }
              if (value[0] === 'delete') {
                dispatch(
                  showConfirmationModal({
                    title: 'Are you sure you want to delete this scenario?',
                    description: 'This action cannot be undone.',
                    onConfirm: async () => {
                      await scenarioInternalApi.deleteScenario({ scenarioHandle: scenario.id! });
                      navigate('/scenarios' + (location.state?.search || ''));
                    },
                    confirmButtonVariant: 'danger',
                    iconColor: IconColor.DANGER,
                    confirmationText: 'Yes, delete',
                    cancelText: 'No',
                  })
                );
              }

              if (value[0] === 'history') {
                setShowHistoryModal(true);
              }
            }}
            placement='bottom right'
          >
            <MenuItem title={'View history'} value={'history'} />
            {archivable(scenario) && canModifyScenario && <MenuItem title={'Archive'} value={'archive'} />}
            {scenario.deletable && canDeleteScenario && (
              <MenuItem title={'Delete'} value={'delete'} dangerTextColor={true} />
            )}
          </SalvMenuButton>
        }
      >
        <div className='scenario-status-section'>
          <div className='d-flex'>
            <div className='text-strong-m m-r-8'>Status</div>
            {statusSwitchers(scenario.status, inReview, isAuthor).label}
          </div>
          <div className='d-flex align-items-end'>
            <div className='text-secondary m-t-4'>{statusSwitchers(scenario.status, inReview, isAuthor).message}</div>

            {statusSwitchers(scenario.status, inReview, isAuthor).actions.map((action, index) => (
              <SalvButton
                key={index}
                variant='secondary'
                size={Size.EXTRA_SMALL}
                label={action.action}
                className='m-l-8'
                onClick={() =>
                  dispatch(
                    showConfirmationModal({
                      title: action.confirmationQuestion,
                      description: action.confirmationDetails,
                      onConfirm: () => {
                        const status = action.nextStatus;
                        if (status === 'REJECTED' || status === 'APPROVED' || status === 'WITHDRAWN') {
                          resolveReviewRequest({
                            entityId: handle,
                            entityType: InternalApiReviewRequestEntityType.SCENARIO,
                            status: status,
                          });
                        }
                        if (status === 'ACTIVE' || status === 'INACTIVE') {
                          updateScenario({
                            id: scenario.id!,
                            internalApiUpdateScenario: {
                              status: status,
                            },
                          });
                        }
                      },
                      confirmButtonVariant: 'danger',
                      iconColor: IconColor.DANGER,
                      confirmationText: action.confirmationText,
                      cancelText: 'No',
                    })
                  )
                }
                disabled={
                  !canModifyScenario ||
                  (scenario.status == InternalApiScenarioStatus.INACTIVE &&
                    !scenario.versions.filter((version) => version.status === InternalApiScenarioVersionStatus.LIVE)
                      .length)
                }
              />
            ))}
          </div>
        </div>

        <div className='scenario-details-section'>
          <div className='details-column-with-scenario-name'>
            <FieldEditor
              label='Internal ID'
              value={scenario.internalId}
              onConfirm={(value) => {
                updateScenario({
                  id: scenario.id!,
                  internalApiUpdateScenario: {
                    internalId: value,
                  },
                });
              }}
              isUpper={true}
              disabled={!canModifyScenario}
            />

            <FieldEditor
              label='Scenario name'
              value={scenario.name}
              fieldWidth={480}
              onConfirm={(value) => {
                updateScenario({
                  id: scenario.id!,
                  internalApiUpdateScenario: {
                    name: value,
                  },
                });
              }}
              disabled={!canModifyScenario}
            />
          </div>

          <div>
            <div>
              {scenario.libraryScenarioHandle ? (
                <FieldView label='Category' value={scenario.category?.name} />
              ) : (
                <FieldEditor
                  label={'Category'}
                  value={scenario.category?.id}
                  options={categories.map((category) => convertToDropdownOption(category.name, category.id))}
                  onConfirm={async (value) => {
                    updateScenario({
                      id: scenario.id!,
                      internalApiUpdateScenario: {
                        categoryId: value,
                      },
                    });
                  }}
                  disabled={!canModifyScenario}
                />
              )}
            </div>

            <div>
              {scenario.libraryScenarioHandle || typologies?.length === 0 ? (
                <FieldView label='Typology' value={scenario.typology?.name} />
              ) : (
                <FieldEditor
                  label='Typology'
                  value={scenario.typology?.id}
                  options={typologies.map((typology) => convertToDropdownOption(typology.name, typology.id))}
                  onConfirm={(value) => {
                    updateScenario({
                      id: scenario.id!,
                      internalApiUpdateScenario: {
                        typologyId: value,
                      },
                    });
                  }}
                  disabled={!canModifyScenario}
                />
              )}
            </div>
          </div>
        </div>

        <div className='d-flex'>
          <div className='scenario-card'>
            <div className='content'>
              <FieldView label='Scenario type' value={monitoringScenarioTypes[scenario.type]} />
              <FieldView
                label='Alerting entity'
                value={<span className='text-capitalize'>{scenario.entityType.toLowerCase()}</span>}
              />
              {scenario.alertedPersonCategory && isCounterpartyEnabled && (
                <FieldView
                  label='Person type'
                  value={<span className='text-capitalize'>{scenario.alertedPersonCategory.toLowerCase()}</span>}
                />
              )}
            </div>
          </div>
          <div className='scenario-card m-l-16'>
            <div className='content'>
              <FieldView
                label={
                  <div className='d-flex align-items-center'>
                    True positive rate{' '}
                    <SalvTooltipWrapper
                      tooltip={`
                        Data is shown for the last 30 days.
                        It shows the percentage of true positive alerts from all closed alerts. 
                        Highlighted percentage is the change of the rate compared to the preceding 90 days.
                        `}
                      placement='left'
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer text-secondary m-l-4' />
                    </SalvTooltipWrapper>
                  </div>
                }
                value={
                  <>
                    <span className='text-capitalize'>
                      {(scenarioMetrics30?.truePositivePercentage ?? 0).toFixed(2)}%
                    </span>

                    <span className={getChangeClass(truePositiveChange)}>
                      {renderPercentChange(truePositiveChange)}
                    </span>
                  </>
                }
              />
              <FieldView
                label={
                  <div className='d-flex align-items-center'>
                    Meaningful alert rate{' '}
                    <SalvTooltipWrapper
                      tooltip={`
                        Data is shown for last 30 days.
                        An alert is considered meaningful if it is closed and any of the following apply: (a) 2+ notes added, (b) file attached, (c) 2+ assignees, (d) Bridge investigation created from the alert, (e) final status as true positive, (f) 3+ status changes. 
                        Highlighted percentage is the change of the rate compared to the preceding 90 days.
                        `}
                      placement='left'
                    >
                      <FontAwesomeIcon icon={faCircleInfo} className='cursor-pointer text-secondary m-l-4' />
                    </SalvTooltipWrapper>
                  </div>
                }
                value={
                  <>
                    <span className='text-capitalize'>
                      {(scenarioMetrics30?.meaningfulAlertPercentage ?? 0).toFixed(2)}%
                    </span>

                    <span className={getChangeClass(meaningfulAlertChange)}>
                      {renderPercentChange(meaningfulAlertChange)}
                    </span>
                  </>
                }
              />
            </div>
          </div>
        </div>

        <div className='m-t-24'>
          <ScenarioVersions scenario={scenario} />
        </div>

        <ScenarioHistoryModal
          scenario={scenario}
          showModal={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
        />
      </SalvPage>
    )
  );
};

export default MonitoringScenario;
